export type FeatureMode = 'drink' | 'door' | 'parking' | 'room' | 'staff';
export type DrinkView = 'menu' | 'cart';
export type RoomNo = '503' | '505';
export type DoorNo = 1 | 2;

export interface MeetingRoom {
    id: RoomNo;
    name: string;
    places: string[];
}

export interface MeetingLocation {
    room: RoomNo;
    roomName: string;
    place: string;
    label: string;
}

export interface MeetingContext {
    meetingId: string;
    reservationId: string;
    meetingTitle: string;
}

export interface ClockText {
    timeText: string;
    dateText: string;
}

export const MEETING_ROOMS: MeetingRoom[] = [
    { id: '503', name: '503호', places: ['회의실', '세미나실'] },
    { id: '505', name: '505호', places: ['대표실', '회의실'] }
];

const MEETING_LOCATION_STORAGE_KEY = 'meeting-room-kiosk-location';
const MEETING_CONTEXT_STORAGE_KEY = 'meeting-room-kiosk-meeting';

export class KioskContext {
    public static featurePath(mode: FeatureMode) {
        if (mode === 'drink') return '/kiosk/drink';
        if (mode === 'door') return '/kiosk/door';
        if (mode === 'parking') return '/kiosk/parking';
        if (mode === 'staff') return '/kiosk/staff';
        return '/kiosk/room';
    }

    public static legacyTarget() {
        const params = new URLSearchParams(window.location.search);
        const mode = params.get('mode');
        if (!this.isFeatureMode(mode)) return null;

        params.delete('mode');
        if (mode !== 'drink') params.delete('view');

        const query = params.toString();
        return `${this.featurePath(mode)}${query ? `?${query}` : ''}`;
    }

    public static loadMeetingLocation() {
        const queryLocation = this.loadMeetingLocationFromQuery();
        if (queryLocation) {
            this.storeMeetingLocation(queryLocation);
            return queryLocation;
        }

        try {
            const stored = window.localStorage.getItem(MEETING_LOCATION_STORAGE_KEY);
            if (!stored) return null;

            const parsed = JSON.parse(stored);
            if (!this.isValidMeetingLocation(parsed)) return null;

            const room = MEETING_ROOMS.find((item) => item.id === parsed.room);
            if (!room) return null;

            return {
                room: room.id,
                roomName: room.name,
                place: parsed.place,
                label: `${room.name} ${parsed.place}`
            };
        } catch (e) {
            return null;
        }
    }

    public static storeMeetingLocation(location: MeetingLocation) {
        try {
            window.localStorage.setItem(MEETING_LOCATION_STORAGE_KEY, JSON.stringify(location));
        } catch (e) { }
    }

    public static loadMeetingContext() {
        const params = new URLSearchParams(window.location.search);
        const queryContext = this.normalizeMeetingContext({
            meetingId: params.get('meeting_id') || params.get('meetingId'),
            reservationId: params.get('reservation_id') || params.get('reservationId'),
            meetingTitle: params.get('meeting_title') || params.get('meetingTitle')
        });

        if (this.hasMeetingContext(queryContext)) {
            this.storeMeetingContext(queryContext);
            return queryContext;
        }

        try {
            const stored = window.localStorage.getItem(MEETING_CONTEXT_STORAGE_KEY);
            if (!stored) return this.emptyMeetingContext();

            return this.normalizeMeetingContext(JSON.parse(stored));
        } catch (e) {
            return this.emptyMeetingContext();
        }
    }

    public static storeMeetingContext(context: MeetingContext) {
        if (!this.hasMeetingContext(context)) return;

        try {
            window.localStorage.setItem(MEETING_CONTEXT_STORAGE_KEY, JSON.stringify(context));
        } catch (e) { }
    }

    public static createMeetingLocation(roomNo: RoomNo, place: string) {
        const room = MEETING_ROOMS.find((item) => item.id === roomNo);
        if (!room || room.places.indexOf(place) < 0) return null;

        return {
            room: room.id,
            roomName: room.name,
            place,
            label: `${room.name} ${place}`
        };
    }

    public static getRoomPlaces(roomNo: RoomNo | null) {
        const room = MEETING_ROOMS.find((item) => item.id === roomNo);
        return room ? room.places : [];
    }

    public static getDoorNo(roomNo: RoomNo | null): DoorNo | null {
        if (roomNo === '503') return 1;
        if (roomNo === '505') return 2;
        return null;
    }

    public static clockText(): ClockText {
        const now = new Date();
        const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
        const hour = `${now.getHours()}`.padStart(2, '0');
        const minute = `${now.getMinutes()}`.padStart(2, '0');

        return {
            timeText: `${hour}:${minute}`,
            dateText: `${now.getMonth() + 1}월 ${now.getDate()}일 (${weekdays[now.getDay()]})`
        };
    }

    public static hasMeetingContext(context: MeetingContext) {
        return !!(context.meetingId || context.reservationId || context.meetingTitle);
    }

    private static isFeatureMode(mode: string | null): mode is FeatureMode {
        return mode === 'drink' || mode === 'door' || mode === 'parking' || mode === 'room' || mode === 'staff';
    }

    private static loadMeetingLocationFromQuery() {
        const params = new URLSearchParams(window.location.search);
        const room = this.toRoomNo(params.get('room') || params.get('room_no') || params.get('roomNo'));
        const place = this.toText(params.get('place') || params.get('place_name') || params.get('placeName'));
        if (!room || !place) return null;

        return this.createMeetingLocation(room, place);
    }

    private static toRoomNo(value: any): RoomNo | null {
        const room = this.toText(value).replace(/호$/, '');
        if (room === '503' || room === '505') return room;

        return null;
    }

    private static isValidMeetingLocation(value: any): value is MeetingLocation {
        if (!value || !value.room || !value.place) return false;

        return MEETING_ROOMS.some((room) => {
            return room.id === value.room && room.places.indexOf(value.place) >= 0;
        });
    }

    private static normalizeMeetingContext(value: any): MeetingContext {
        return {
            meetingId: this.toText(value?.meetingId || value?.meeting_id),
            reservationId: this.toText(value?.reservationId || value?.reservation_id),
            meetingTitle: this.toText(value?.meetingTitle || value?.meeting_title)
        };
    }

    private static emptyMeetingContext(): MeetingContext {
        return { meetingId: '', reservationId: '', meetingTitle: '' };
    }

    private static toText(value: any) {
        if (value === null || value === undefined) return '';
        return `${value}`.trim();
    }
}
